'use client';
import { useState, useMemo, useRef } from 'react';
import { useToast } from '@practium/ui';
import { useGetGroups, useCreateHierarchy, useUpdateHierarchy } from '@practium/features/api';
import type { HierarchyFormData } from '../types';
import { HierarchySchema, HIERARCHY_FIELD_LABELS } from '../schemas/system.schema';
import { DEFAULT_HIERARCHY_FORM } from '../constants/system.constants';

export interface UseHierarchyFormOptions {
	id?: string;
	initialData?: HierarchyFormData;
	onSubmit: () => void;
}

export const useHierarchyForm = ({ id, initialData, onSubmit }: UseHierarchyFormOptions) => {
	const toast = useToast();
	const { mutateAsync: createHierarchy } = useCreateHierarchy();
	const { mutateAsync: updateHierarchy } = useUpdateHierarchy();
	const initialForm = initialData ? { ...DEFAULT_HIERARCHY_FORM, ...initialData } : { ...DEFAULT_HIERARCHY_FORM };
	const [form, setForm] = useState<HierarchyFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const { data: groupDTOs } = useGetGroups();
	const groupOptions = useMemo(
		() => groupDTOs.map(g => ({ value: g._id, label: g.name })),
		[groupDTOs]
	);

	const set = <K extends keyof HierarchyFormData>(key: K, value: HierarchyFormData[K]) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = HierarchySchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => HIERARCHY_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			const payload = {
				name: form.name,
				role: '',
				description: form.description,
				farmer: { group: form.farmer || null },
				consultant: { group: form.consultant || null },
				company: { group: form.company || null },
				master: { group: form.master || null },
			};
			if (id) {
				await updateHierarchy({ hierarchyId: id, ...payload });
			} else {
				await createHierarchy(payload);
			}
			toast.success('Hierarquia salva com sucesso!');
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar hierarquia');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, groupOptions, errors, isLoading, set, handleSubmit, isDirty };
};
